import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiUrl, fetchJson } from "@/lib/api";
import { gaTrack } from "@/lib/analytics";
import { trackEvent } from "@/lib/tracker";
import { usePostingImpressions } from "@/lib/impressions";
import { fetchWithAuth, isAuthenticated } from "../utils/fetchWithAuth";
import MarketHeader, { MarketSearch } from "../components/MarketHeader";
import MarketRow, { MarketRowSkeleton } from "../components/MarketRow";
import { ASK_PATH, CATEGORIES, CHEV_BG, MARKET_CSS, STEPS } from "@/lib/marketBrowse";
import { MARKET_ROW_CSS, fromOrgPosting, fromPersonPosting, fromServiceCard, isOwnRow, today } from "@/lib/marketRows";
import PhIcon from "../components/PhIcon";
import SEO from "../components/SEO";

/* The homepage, since 2026-10-31: a marketplace you can browse without an
   account. People who posted when they can start, services from companies and
   independent pros, and open jobs, in one list.

   Design came from the "Neutral Homepage Concepts" canvas, board M7b4. The
   business page it replaced still lives at /employers (ads point there), and
   the talent page at /contractors and /for-talent.

   Nothing here asks for an account until somebody acts. Message, Apply and
   Request go to the listing's own page, which handles sign-in at that moment.
   Every "Get started" opens the free-form ask (ASK_PATH, below). */




const PAGE = 15;

/* One browse component, several addresses (BROWSE_CONSOLIDATION_PLAN.md). Each
   address keeps its own title and SEO and opens on its own default filter;
   everything after that is the same page. `surface` tags the analytics, so the
   homepage test is not muddied by browse traffic. */
const PRESETS = {
  home: {
    surface: "home",
    title: "Ready to start",
    titleLinks: true,
    sub: "See people available to work, services from companies and independent pros, and open jobs.",
    seo: {
      title: "People, services and work, ready to start",
      description: "Browse people who posted when they can start, services from local companies and independent pros, and open jobs. Then run the contract, schedule, timecards and pay in the same place.",
      path: "/",
    },
  },
  browse: {
    surface: "browse",
    title: "Ready to start",
    titleLinks: true,
    sub: "See people available to work, services from companies and independent pros, and open jobs.",
    seo: {
      title: "Browse people, services and jobs",
      description: "People who posted when they can start, services from companies and independent pros, and open jobs on Sift. Then run the contract, schedule, timecards and pay in the same place.",
      path: "/browse",
    },
  },
  services: {
    surface: "services",
    title: "Services",
    sub: "Companies and independent pros you can request work from.",
    kinds: { person: false, service: true, job: false },
    seo: {
      title: "Find Services Near You",
      description: "Browse local service providers across healthcare, trades, home services, professional services, and more. Workforce coordination made simple.",
      path: "/services",
    },
  },
  postings: {
    surface: "postings",
    title: "Open posts",
    sub: "People available to work and companies hiring.",
    kinds: { person: true, service: false, job: true },
    seo: {
      title: "Job Postings",
      description: "Find open contract and direct-hire postings across healthcare, trades, transportation, professional services, and more. Apply directly through Sift.",
      path: "/postings",
    },
  },
};

const ALL_KINDS = { person: true, service: true, job: true };
const SHOWS = ["all", "person", "service", "job"];

/* Where a link lands. `?type=` is the old /services tab and is still linked
   from the app and Google, so it maps onto the new filters. */
function initialFilters(preset, params) {
  let show = SHOWS.includes(params.get("show")) ? params.get("show") : (preset.show || "all");
  let kinds = { ...ALL_KINDS, ...(preset.kinds || {}) };
  const type = (params.get("type") || "").toLowerCase();
  if (type === "contractor" || type === "org") kinds = { person: false, service: true, job: false };
  if (type === "posting") { show = "all"; kinds = { person: true, service: false, job: true }; }
  const cat = CATEGORIES.find((c) => c.key === params.get("category")) || CATEGORIES[0];
  const text = (params.get("q") || "").trim();
  const location = (params.get("location") || "").trim();
  return { show, kinds, category: text ? "all" : cat.key, text, location, q: text || cat.q };
}

// Person, service, job, person... so the first screen shows every side of the
// marketplace instead of fifteen of whichever source answered first.
function interleave(...lists) {
  const out = [];
  const max = Math.max(0, ...lists.map((l) => l.length));
  for (let i = 0; i < max; i += 1) lists.forEach((l) => { if (l[i]) out.push(l[i]); });
  return out;
}

export default function MarketHome({ surface: page = "home" }) {
  const preset = PRESETS[page] || PRESETS.home;
  const [params, setParams] = useSearchParams();
  const [start] = useState(() => initialFilters(preset, params));
  const surface = preset.surface;
  const loggedIn = isAuthenticated();

  const [show, setShow] = useState(start.show); // header select: all | person | service | job
  const [text, setText] = useState(start.text);
  const [where, setWhere] = useState(start.location);
  const [category, setCategory] = useState(start.category);
  const [query, setQuery] = useState({ q: start.q, location: start.location });

  const [kinds, setKinds] = useState(start.kinds);
  const [startWindow, setStartWindow] = useState("any"); // week | next | any
  const [howOpen, setHowOpen] = useState(false); // "How Sift works" pop-out

  const [rows, setRows] = useState(null); // null = loading
  const [me, setMe] = useState(null); // signed in: whose cards are theirs
  const [visible, setVisible] = useState(PAGE);

  useEffect(() => {
    trackEvent("market_home_viewed", { surface });
  }, [surface]);

  useEffect(() => {
    if (!loggedIn) return undefined;
    let alive = true;
    fetchWithAuth("/api/accounts/me/").then((d) => { if (alive) setMe(d); }).catch(() => {});
    return () => { alive = false; };
  }, [loggedIn]);

  // Escape closes whichever overlay is open.
  useEffect(() => {
    if (!howOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setHowOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [howOpen]);

  const openHow = (from) => {
    setHowOpen(true);
    trackEvent("market_home_how_opened", { from, surface });
  };

  useEffect(() => {
    let alive = true;
    setRows(null);
    setVisible(PAGE);
    const params = (extra) => {
      const p = new URLSearchParams(extra);
      if (query.q) p.set("q", query.q);
      if (query.location) p.set("location", query.location);
      return p.toString();
    };
    // null when a source failed, [] when it answered with nothing. If every
    // source failed the list stays in its loading state rather than claiming
    // "nothing matches": the prerender blocks the API, and that sentence must
    // not be what gets baked into the homepage HTML.
    const safe = (promise) => promise.then((b) => (Array.isArray(b?.results) ? b.results : [])).catch(() => null);
    Promise.all([
      safe(fetchJson(apiUrl(`/api/accounts/public/postings/?${params({ poster_type: "contractor", per_poster: "1", limit: "30" })}`))),
      safe(fetchJson(apiUrl(`/api/accounts/public/services/?${params({ type: "both", limit: "20" })}`))),
      safe(fetchJson(apiUrl(`/api/accounts/public/postings/?${params({ poster_type: "org", limit: "20" })}`))),
    ]).then(([people, services, jobs]) => {
      if (!alive || (people === null && services === null && jobs === null)) return;
      setRows(interleave(
        (people || []).map(fromPersonPosting),
        (services || []).filter((c) => c.slug && c.url).map(fromServiceCard),
        (jobs || []).map(fromOrgPosting),
      ));
    });
    return () => { alive = false; };
  }, [query]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    // Somebody held on an agency's roster is not shown other work: no
    // availability posts and no jobs, the same rule the app's nav follows.
    const held = !!me?.is_held;
    const now = today();
    const weekOut = new Date(now); weekOut.setDate(now.getDate() + 7);
    const twoOut = new Date(now); twoOut.setDate(now.getDate() + 14);
    return rows.filter((r) => {
      if (!kinds[r.kind]) return false;
      if (held && r.kind !== "service") return false;
      if (show !== "all" && r.kind !== show) return false;
      // The start filter is about people. A service or a job has no start
      // date of its own to compare, so it passes rather than vanishing.
      if (r.kind === "person" && r.start) {
        if (startWindow === "week" && r.start > weekOut) return false;
        if (startWindow === "next" && (r.start <= weekOut || r.start > twoOut)) return false;
      }
      return true;
    });
  }, [rows, kinds, show, startWindow, me]);

  const counts = useMemo(() => {
    const c = { person: 0, service: 0, job: 0 };
    (rows || []).forEach((r) => { c[r.kind] += 1; });
    return c;
  }, [rows]);

  useEffect(() => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      const put = (k, v) => { if (v) next.set(k, v); else next.delete(k); };
      next.delete("type");
      put("q", category === "all" ? query.q : "");
      put("location", query.location);
      put("category", category === "all" ? "" : category);
      put("show", show === (preset.show || "all") ? "" : show);
      return next;
    }, { replace: true });
  }, [query, category, show]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (e) => {
    e.preventDefault();
    setCategory("all");
    setQuery({ q: text.trim(), location: where.trim() });
    trackEvent("market_home_search", { has_q: !!text.trim(), has_location: !!where.trim(), show, surface });
  };

  const pickCategory = (c) => {
    setCategory(c.key);
    setText("");
    setQuery((q) => ({ q: c.q, location: q.location }));
    trackEvent("market_home_category", { category: c.key, surface });
  };

  const applyWhere = (e) => {
    e.preventDefault();
    setQuery((q) => ({ ...q, location: where.trim() }));
  };

  // One search, two places: in the green header on desktop, and under the
  // "Ready to start" title on phones, where the header has no room for it.
  const searchProps = { show, setShow, text, setText, where, setWhere, onSubmit: onSearch };

  const listRef = useRef(null);
  const shownIds = filtered.slice(0, visible).map((r) => r.postingId).filter(Boolean).join(",");
  usePostingImpressions(listRef, [shownIds]);

  const currentCat = CATEGORIES.find((c) => c.key === category) || CATEGORIES[0];
  // A typed search or a place is a search; a category is a section with a name.
  const searching = (category === "all" && !!query.q) || !!query.location;
  const onRowClick = (r, i) => trackEvent("market_home_listing_click", { kind: r.kind, position: i, surface });
  // The empty state's two doors carry what they searched for into the post
  // composer; signed-out visitors make the account first and land there.
  const composeQ = query.q && category === "all" ? `?compose=${encodeURIComponent(query.q)}` : "";
  const signupFirst = (target) => (loggedIn ? target : `/auth/register?next=${encodeURIComponent(target)}`);
  const getStarted = (location) => gaTrack("cta_get_started", { location, account_type: "unknown" });

  return (
    <div className="mh mh-page">
      <style>{MARKET_CSS + MARKET_ROW_CSS}</style>
      <SEO
        title={preset.seo.title}
        description={preset.seo.description}
        path={preset.seo.path}
      />

      <MarketHeader
        search={searchProps}
        category={category}
        onCategory={pickCategory}
        onHow={openHow}
        surface={surface}
      />

      <section aria-label="How Sift works" className="mh-how">
        <div className="mh-wrap mh-how-in">
          <p className="mh-how-label">How Sift works<span>Find someone, then run the work here</span></p>
          <ol className="mh-chev">
            {STEPS.map(([t, l], i) => (
              <li key={t} style={{ background: CHEV_BG[i] }}>
                <span className="mh-chev-n">{i + 1}</span>
                <span className="mh-chev-t">{t}<span>{l}</span></span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <main id="market" className="mh-wrap mh-main">
        <aside aria-label="Filters" className="mh-filters">
          <div className="mh-fhead">
            <span>Filters</span>
            <button
              type="button"
              onClick={() => { setKinds({ person: true, service: true, job: true }); setStartWindow("any"); setShow("all"); }}
            >
              Clear all
            </button>
          </div>
          <div className="mh-fsec">
            <p className="mh-fh">Show</p>
            {[["person", "People available"], ["service", "Services"], ["job", "Open jobs"]].map(([k, label]) => (
              <label key={k} className="mh-frow">
                <input type="checkbox" checked={kinds[k]} onChange={() => setKinds((s) => ({ ...s, [k]: !s[k] }))} />
                <span className="mh-fbox" aria-hidden="true"><PhIcon name="check" size={12} /></span>
                <span className="mh-grow">{label}</span>
                <span className="mh-fcount">{rows ? counts[k] : ""}</span>
              </label>
            ))}
          </div>
          <div className="mh-fsec">
            <p className="mh-fh">Can start</p>
            <div className="mh-chips">
              {[["week", "This week"], ["next", "Next week"], ["any", "Any time"]].map(([k, label]) => (
                <button key={k} type="button" className={`mh-chip${startWindow === k ? " on" : ""}`} aria-pressed={startWindow === k} onClick={() => setStartWindow(k)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <form className="mh-fsec" onSubmit={applyWhere}>
            <p className="mh-fh">Where</p>
            <label className="mh-input">
              <PhIcon name="map-pin" size={16} />
              <input value={where} onChange={(e) => setWhere(e.target.value)} placeholder="City, state or remote" aria-label="Location" />
            </label>
            <p className="mh-fnote">Remote postings stay in the results.</p>
          </form>
          <div className="mh-fsec mh-fsec-last">
            <p className="mh-fh">Offering your work?</p>
            <p className="mh-fnote" style={{ marginTop: 0 }}>Post when you can start, or list a service people can request.</p>
            <Link to="/auth/register" className="mh-flink">Post your availability</Link>
          </div>
        </aside>

        <div className="mh-results">
          <div className="mh-rhead">
            <div className="mh-rhead-row">
              {/* "Ready to start" is also the way in, same as the header button.
                  A search result heading is just a heading. */}
              <h1>
                {searching ? "Results" : category !== "all" ? currentCat.label : !preset.titleLinks ? preset.title : (
                  <Link to={ASK_PATH} className="mh-title-link" onClick={() => getStarted(`${surface}_title`)}>
                    {preset.title}<PhIcon name="arrow-right" size={22} />
                  </Link>
                )}
              </h1>
              <button type="button" className="mh-howlink" onClick={() => openHow("title")}>How Sift works</button>
            </div>
            <p>{preset.sub}</p>
            <MarketSearch className="mh-search-body" {...searchProps} />
          </div>

          {rows === null ? (
            <ul className="mr-list" aria-busy="true" aria-label="Loading listings">
              {[0, 1, 2, 3, 4, 5].map((n) => <MarketRowSkeleton key={n} wide={n % 2 === 0} />)}
            </ul>
          ) : filtered.length === 0 ? (
            <div className="mh-empty">
              <p>{query.q && category === "all" ? `Nothing for “${query.q}” yet.` : "Nothing matches yet."}</p>
              <p>
                Post it as a need and people who fit will see it, or offer it yourself and get found. Or{" "}
                <button type="button" onClick={() => { setCategory("all"); setText(""); setWhere(""); setQuery({ q: "", location: "" }); setKinds({ ...ALL_KINDS, ...(preset.kinds || {}) }); setStartWindow("any"); setShow(preset.show || "all"); }}>
                  clear the search
                </button>.
              </p>
              <div className="mh-doors">
                <Link to={signupFirst(`/company/postings${composeQ}`)} className="mh-btn mh-btn-green" onClick={() => trackEvent("market_home_empty_door", { door: "need", surface })}>Post this as a need</Link>
                <Link to={signupFirst(`/dashboard/contractor/postings${composeQ}`)} className="mh-btn mh-btn-line" onClick={() => trackEvent("market_home_empty_door", { door: "offer", surface })}>Offer this</Link>
              </div>
            </div>
          ) : (
            <>
              <ul className="mr-list" ref={listRef}>
                {filtered.slice(0, visible).map((r, i) => (
                  <MarketRow key={r.id} r={r} own={isOwnRow(r, me)} onOpen={() => onRowClick(r, i)} />
                ))}
              </ul>
              {visible < filtered.length && (
                <div className="mh-more">
                  <button type="button" className="mh-btn mh-btn-line" onClick={() => setVisible((v) => v + PAGE)}>Show more</button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {howOpen && (
        <div className="mh-pop-scrim">
          <button type="button" className="mh-pop-backdrop" aria-label="Close" tabIndex={-1} onClick={() => setHowOpen(false)} />
          <div role="dialog" aria-modal="true" aria-labelledby="mh-pop-h" className="mh-pop">
            <div className="mh-pop-head">
              <h2 id="mh-pop-h">How Sift works</h2>
              <button type="button" aria-label="Close" onClick={() => setHowOpen(false)} autoFocus><PhIcon name="x" size={20} /></button>
            </div>
            <p className="mh-pop-sub">Find someone here, then run the work in the same place.</p>
            <ol className="mh-vchev">
              {STEPS.map(([t, l], i) => (
                <li key={t} style={{ background: CHEV_BG[i] }}>
                  <span className="mh-chev-n">{i + 1}</span>
                  <span className="mh-chev-t">{t}<span>{l}</span></span>
                </li>
              ))}
            </ol>
            <Link to={ASK_PATH} className="mh-btn mh-btn-green mh-pop-cta" onClick={() => getStarted("market_home_how")}>Get started</Link>
          </div>
        </div>
      )}
    </div>
  );
}


